import React from 'react';
import { IconFileDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { IconButton, TotemButton } from 'ui/Button';

import { InvoicesTableStyle, InvoiceStatus, SettingsSubtitle } from 'styles/pages/settings.styles';
import { DISPLAY_DATE_FORMAT } from 'constants/date-constants';
import { GetInvoicesQuery, useGetInvoicePdfBase64LazyQuery } from 'data/__generated__';

type Invoice = GetInvoicesQuery['getInvoices']['invoices'][number];

// Ouvre un PDF fourni en base64 (factures sans URL directe, ex. Odoo) dans un nouvel onglet.
const openBase64Pdf = (base64: string): void => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) {
        byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
};

const formatEuros = (value?: number | null): string => (value != null ? `${value.toFixed(2)}€` : '');

interface InvoicesTableProps {
    invoices: Invoice[] | undefined[];
    fetchMore: (args: any) => void;
    hasMore: boolean;
    organizationId: string;
    isLoading: boolean;
}
export const InvoicesTable: React.FC<InvoicesTableProps> = ({
    invoices,
    fetchMore,
    hasMore,
    organizationId,
    isLoading,
}) => {
    const paymentInfo = {
        deleted: ['Annulée', 'darkGrey'],
        draft: ['En cours', 'pantinGrey'],
        open: ['En attente', 'red'],
        paid: ['Réglée', 'green'],
        uncollectible: ['A compléter', 'orange'],
        void: ['Nulle', 'yellow'],
    };

    const [fetchInvoicePdf] = useGetInvoicePdfBase64LazyQuery();

    // Source-agnostique : URL directe si disponible (Stripe), sinon récupération du PDF base64 à la demande (Odoo).
    const handleDownload = async (invoice: Invoice): Promise<void> => {
        if (invoice.pdfUrl) {
            window.open(invoice.pdfUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        const { data } = await fetchInvoicePdf({ variables: { organizationId, invoiceId: invoice.id } });
        const base64 = data?.getInvoicePdfBase64;
        if (base64) {
            openBase64Pdf(base64);
        }
    };

    const sortedInvoices = [...invoices].sort((a, b): number => (b?.created ?? 0) - (a?.created ?? 0)) as Invoice[];

    if (sortedInvoices?.length === 0) {
        return <SettingsSubtitle $topMargin="15px">Pas de facture pour le moment</SettingsSubtitle>;
    }
    return (
        <>
            <InvoicesTableStyle>
                <thead>
                    <tr>
                        <th>Statut</th>
                        <th>Description</th>
                        <th>Date</th>
                        <th>Numéro</th>
                        <th>Prix HT</th>
                        <th>TVA</th>
                        <th>Prix TTC</th>
                        <th>Facture</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedInvoices
                        .filter((invoice) => invoice?.status !== 'deleted')
                        .map((invoice) => {
                            const invoiceDate = dayjs(invoice.created * 1000).format(DISPLAY_DATE_FORMAT);
                            // Use 'draft' as default if status is null/undefined
                            const [status, color] =
                                paymentInfo[(invoice?.status ?? 'draft') as keyof typeof paymentInfo];
                            return (
                                <tr key={invoice.id}>
                                    <td>{invoice.status && <InvoiceStatus color={color}>{status}</InvoiceStatus>}</td>
                                    <td>{invoice.description}</td>
                                    <td>{invoiceDate}</td>
                                    <td>{invoice.number}</td>
                                    <td>{formatEuros(invoice.subtotal)}</td>
                                    <td>{formatEuros(invoice.tax)}</td>
                                    <td>{formatEuros(invoice.total)}</td>
                                    <td width="5%">
                                        <IconButton onClick={() => handleDownload(invoice)}>
                                            <IconFileDownload />
                                        </IconButton>
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </InvoicesTableStyle>
            {hasMore && (
                <TotemButton
                    as="a"
                    $margins={[1, 0]}
                    $type="secondary"
                    $alignSelf="center"
                    $fontSize="0.75em"
                    onClick={() => {
                        fetchMore({
                            variables: {
                                offset: invoices.length,
                                organizationId,
                            },
                        });
                    }}
                    disabled={isLoading}
                >
                    Voir plus
                </TotemButton>
            )}
        </>
    );
};
